import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import RoleService from '@/services/UsersServices/RoleService';
import { extractError } from '@/helpers/extractApiError';

const model = {
    data: null,
    submitting: false,
    errors: null,
    successOnCreate: '',
    loading: false,
    permissionList: [],
};

export const viewRole = createAsyncThunk(
    'role/viewRole',
    async (roleId, { rejectWithValue }) => {
        try {
            const response = await RoleService.viewRole(roleId);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const createRole = createAsyncThunk(
    'role/createRole',
    async (data, { rejectWithValue }) => {
        try {
            const { id: roleId } = data;
            let response;
            if (roleId) {
                response = await RoleService.updateRole(data, roleId);
            } else {
                response = await RoleService.createRole(data);
            }
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const getPermissionList = createAsyncThunk(
    'role/getPermissionList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await RoleService.getPermissionList();
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

const createRoleSlice = createSlice({
    name: 'createRole',
    initialState: model,
    reducers: {
        resetRoleStateData: (state) => {
            state.data = null;
        },
        resetRoleStateOnError: (state) => {
            state.errors = null;
        },
        resetRoleStateOnSuccess: (state) => {
            state.successOnCreate = '';
        },
    },
    extraReducers: (builder) => {
        // view role
        builder.addCase(viewRole.pending, (state) => {
            state.loading = true;
        }).addCase(viewRole.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload;
        }).addCase(viewRole.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // create role
        builder.addCase(createRole.pending, (state) => {
            state.submitting = true;
        }).addCase(createRole.fulfilled, (state, action) => {
            state.submitting = false;
            const roleId = action.meta.arg.id;
            state.successOnCreate = roleId ? 'Role updated successfully!' : 'Role created successfully!';
        }).addCase(createRole.rejected, (state, action) => {
            state.submitting = false;
            state.errors = action.payload;
        });

        // get permission list
        builder.addCase(getPermissionList.pending, (state) => {
            state.loading = true;
        }).addCase(getPermissionList.fulfilled, (state, action) => {
            state.loading = false;
            state.permissionList = action.payload;
        }).addCase(getPermissionList.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });
    },
});

export const {
    resetRoleStateData,
    resetRoleStateOnError,
    resetRoleStateOnSuccess,
} = createRoleSlice.actions;

export default createRoleSlice.reducer;
